import { Box, LinearProgress, Tab, Tabs } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { read, utils } from 'xlsx';
import { experiment_file_validator } from './utils/experiment_file_validator';
import { useSelector } from 'react-redux';
import { get_selected_plant_name } from '../../redux/auth/auth.selectors';
import { v4 } from "uuid";
import SnackbarWithMessage from '../../common/components/Snackbars/SnackbarWithMessage';
import { FileUploaderRipik } from '../HomePage/components/FileUploaderRipik';
import FileViewer from '../HomePage/components/FileViewer';
import { createPaginatedData } from '../../common/components/Chart/utils/pagination_data_creator';
import FullyIntegratedTimelineChart from '../../common/components/Chart/FullyIntegratedTimelineChart';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import OptimusTabPanel from '../../common/components/TabUI/TabPanel';
import { calculateRemainingHeight } from '../../util';
import { updload_and_share_data } from './utils/share_view';
import { LoadingButton } from '@mui/lab';



const Experiment = () => {

  const [initial_load, set_initial_load] = useState(true);

  const { selected_plant } = useSelector(get_selected_plant_name);
  const [value, setValue] = React.useState(0);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const [experiment_files, set_experiment_files] = useState({
    machine_view: {
      file: null,
      data: null,
      columns: null,
      table_data: null,
      paginated_data: null,
      paginated_data_copy: null,
      formatted_json_data: null
    },
    product_view: {
      file: null,
      data: null,
      columns: null,
      table_data: null,
      paginated_data: null,
      paginated_data_copy: null,
      formatted_json_data: null
    },
    analyst_view: {
      file: null,
      data: null,
      columns: null,
      table_data: null,
      paginated_data: null,
      paginated_data_copy: null,
      formatted_json_data: null
    },
    sla_matrix: {
      file: null,
      data: null,
      columns: null,
      table_data: null
    }
  });

  const [selected_primary_names_machine_view, set_selected_primary_names_machine_view] = useState([]);
  const [selected_primary_names_analyst_view, set_selected_primary_names_analyst_view] = useState([]);
  const [selected_primary_names_product_view, set_selected_primary_names_product_view] = useState([]);

  const [selected_secondary_names_machine_view, set_selected_secondary_names_machine_view] = useState({
    "product_code": [],
    "test_id": []
  });
  const [selected_secondary_names_analyst_view, set_selected_secondary_names_analyst_view] = useState({
    "spec_id": [],
    "test_id": []
  });
  const [selected_secondary_names_product_view, set_selected_secondary_names_product_view] = useState({
    "analyst_id": [],
    "test_id": []
  });

  const [filtered_in, set_filtered_in] = useState({
    machine_view: {
      primary: false,
      secondary: false,
    },
    analyst_view: {
      primary: false,
      secondary: false,
    },
    product_view: {
      primary: false,
      secondary: false,
    },
  });

  useEffect(() => {
    console.log({ filtered_in });
    if (filtered_in.machine_view.primary) {
      set_selected_secondary_names_machine_view({
        "product_code": [],
        "test_id": []
      });
    } else if (filtered_in.machine_view.secondary) {
      set_selected_primary_names_machine_view([]);
    }
    if (filtered_in.analyst_view.primary) {
      set_selected_secondary_names_analyst_view({
        "spec_id": [],
        "test_id": []
      });
    } else if (filtered_in.analyst_view.secondary) {
      set_selected_primary_names_analyst_view([]);
    }
    if (filtered_in.product_view.primary) {
      set_selected_secondary_names_product_view({
        "analyst_id": [],
        "test_id": []
      });
    } else if (filtered_in.product_view.secondary) {
      set_selected_primary_names_product_view([]);
    }
  }, [filtered_in]);

  const [open_snackbar_with_message, set_open_snackbar_with_message] = useState({
    open: false,
    message: "",
    severity: "info"
  });

  const [loading_states, set_loading_states] = useState({
    machine_view: false,
    product_view: false,
    analyst_view: false,
    sla_matrix: false
  });

  const [color_by, set_color_by] = useState("spec_id");

  const color_by_options = ["spec_id", "machine_id", "analyst_required", "test_id", "step_name"];

  const update_color_by = (new_color_by) => {
    set_color_by(new_color_by);
  };

  const [updating_chart_data, set_updating_chart_data] = useState(false);

  const handle_experiment_file_upload = (
    key_to_be_updated,
    file,
    has_chart
  ) => {
    if (file) {
      set_initial_load(false);
      //   console.log("reading uploaded file");
      set_loading_states(prev => ({
        ...prev,
        [key_to_be_updated]: true
      }));
      set_experiment_files((prev) => {
        const temporary_obj = { ...prev };
        //   console.log(temporary_obj, temporary_obj[monthly_or_master]);
        temporary_obj[key_to_be_updated].file = null;
        temporary_obj[key_to_be_updated].columns = null;
        temporary_obj[key_to_be_updated].table_data = null;
        temporary_obj[key_to_be_updated].status = null;
        temporary_obj[key_to_be_updated].paginated_data = null;
        temporary_obj[key_to_be_updated].paginated_data_copy = null;
        temporary_obj[key_to_be_updated].formatted_json_data = null;
        return temporary_obj;
      });
      const reader = new FileReader();
      reader.onload = (event) => {
        const workbook = read(event.target?.result, { type: "binary", cellText: false, cellDates: true });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = utils.sheet_to_json(worksheet, { header: 1 });

        // Convert to array of objects
        const headers = jsonData[0];
        const validation = experiment_file_validator(selected_plant, key_to_be_updated, headers);
        // console.log({ validation });
        if (!validation.isValid) {
          setTimeout(() => {
            set_open_snackbar_with_message({
              message: `Uploaded file is missing the required column(s): \n${validation?.missing_keys.join(", ")}`,
              open: true,
              severity: "error"
            });
            set_loading_states(prev => ({
              ...prev,
              [key_to_be_updated]: false
            }));
          }, 1000);
          return;
        }
        const rows = jsonData.slice(1);
        // console.log(jsonData);
        const formattedData = rows.map((row) => {
          const obj = {};
          headers.forEach((header, index) => {
            if (header === "start_time" || header === "end_time") {
              // Convert date and time to JavaScript Date object
              // console.log(row[index])
              obj[header] = new Date(row[index]);
            } else {
              obj[header] = row[index];
            }
          });
          obj.id = v4();
          return obj;
        });
        let paginated_data = null;
        let keyname = "machine_id";
        // let colorBy = "spec_id";
        switch (key_to_be_updated) {
          case "machine_view":
            keyname = "machine_id";
            // colorBy = "spec_id";
            break;
          case "analyst_view":
            keyname = "analyst_id";
            // colorBy = "spec_id";
            break;
          case "product_view":
            keyname = "batch_no";
            // colorBy = "spec_id";
            break;
          default:
            keyname = "machine_id";
        }
        if (has_chart) {
          // console.log("paginating");
          paginated_data = createPaginatedData(keyname, formattedData, color_by);
          // console.log({ paginated_data });
        }
        const columns = headers.map((header) => {
          if (header === "start_time" || header === "end_time") {
            return ({
              field: header,
              flex: 1,
              type: "dateTime"
            });
          } else if (header === "analyst_required") {
            return ({
              field: header,
              flex: 1,
              type: "boolean"
            });
          }
          return ({
            field: header,
            flex: 1,
          });
        });
        columns.push({ field: "id" });
        const table_data = formattedData.map(item => ({ ...item, id: v4() }));
        setTimeout(() => {
          set_experiment_files((prev) => {
            const temporary_obj = { ...prev };
            //   console.log(temporary_obj, temporary_obj[monthly_or_master]);
            temporary_obj[key_to_be_updated].file = file;
            temporary_obj[key_to_be_updated].columns = columns;
            temporary_obj[key_to_be_updated].table_data = table_data;
            temporary_obj[key_to_be_updated].status = true;
            temporary_obj[key_to_be_updated].paginated_data = paginated_data;
            temporary_obj[key_to_be_updated].paginated_data_copy = paginated_data;
            temporary_obj[key_to_be_updated].formatted_json_data = formattedData;
            return temporary_obj;
          });
          set_loading_states(prev => ({ ...prev, [key_to_be_updated]: false }));
        }, 2000);
      };
      reader.readAsBinaryString(file);
    }
  };

  useEffect(() => {
    set_selected_primary_names_machine_view([]);
    set_selected_primary_names_analyst_view([]);
    set_selected_primary_names_product_view([]);
    set_updating_chart_data(true);
    set_experiment_files((prev) => {
      const temporary_obj = { ...prev };
      temporary_obj["machine_view"].paginated_data = null;
      temporary_obj["machine_view"].paginated_data_copy = null;
      temporary_obj["analyst_view"].paginated_data = null;
      temporary_obj["analyst_view"].paginated_data_copy = null;
      temporary_obj["product_view"].paginated_data = null;
      temporary_obj["product_view"].paginated_data_copy = null;
      return temporary_obj;
    });
    setTimeout(() => {
      let machine_view_paginated = null;
      if (experiment_files?.machine_view?.formatted_json_data !== null) {
        machine_view_paginated = createPaginatedData("machine_id", experiment_files.machine_view.formatted_json_data, color_by);
        // console.log({ machine_view_paginated });
      }
      let analyst_view_paginated = null;
      if (experiment_files?.analyst_view?.formatted_json_data !== null) {
        analyst_view_paginated = createPaginatedData("analyst_id", experiment_files.analyst_view.formatted_json_data, color_by);
        // console.log({ analyst_view_paginated });
      }
      let product_view_paginated = null;
      if (experiment_files?.product_view?.formatted_json_data !== null) {
        product_view_paginated = createPaginatedData("batch_no", experiment_files.product_view.formatted_json_data, color_by);
        console.log({ product_view_paginated });
      }
      set_experiment_files((prev) => {
        const temporary_obj = { ...prev };
        temporary_obj["machine_view"].paginated_data = machine_view_paginated;
        temporary_obj["machine_view"].paginated_data_copy = machine_view_paginated;
        temporary_obj["analyst_view"].paginated_data = analyst_view_paginated;
        temporary_obj["analyst_view"].paginated_data_copy = analyst_view_paginated;
        temporary_obj["product_view"].paginated_data = product_view_paginated;
        temporary_obj["product_view"].paginated_data_copy = product_view_paginated;
        return temporary_obj;
      });
      set_updating_chart_data(false);
    }, 2000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [color_by]);

  // useEffect(() => {
  //   console.log(experiment_files);
  // }, [experiment_files]);

  const [sharing_loading, set_sharing_loading] = useState(false);

  const handle_upload_and_share = async (view__state_data, selected_secondary_names_chart_view, view_name) => {
    let shareable_url = null;
    set_sharing_loading(true);
    try {
      const response = await updload_and_share_data({ ...view__state_data, selected_secondary_names_chart_view }, view_name);
      if (response.error) {
        console.log("throwing error", response.error);
        throw new Error(response.error);
      }
      else {
        shareable_url = response.shareable_link;
        set_open_snackbar_with_message({
          message: "Generated Shareable URL successfuly",
          open: true,
          severity: "success"
        });
      }
    } catch (error) {
      set_open_snackbar_with_message({
        message: error.toString(),
        open: true,
        severity: "error"
      });
    } finally {
      set_sharing_loading(false);
      const anchor_tag = document.createElement("a");
      anchor_tag.setAttribute("target", "_blank");
      anchor_tag.setAttribute("href", shareable_url);
      anchor_tag.click();
      if (shareable_url !== null) {
        set_open_snackbar_with_message({
          message: "URL copied to clipboard successfuly",
          open: true,
          severity: "success"
        });
      }
    }
  };

  return (
    <Box className="h-[100%]">
      <SnackbarWithMessage {...open_snackbar_with_message} onClose={() => {
        set_open_snackbar_with_message({
          open: false,
          message: "",
          severity: "info"
        });
      }} />
      <Box
        className="p-8"
        sx={{
          width: "100%",
          color: "black",
          fontSize: "28px",
          fontFamily: "Poppins",
          fontWeight: 400,
          lineHeight: "36px",
          wordWrap: "break-word",
        }}
      >
        QC Scheduler
        <br />
      </Box>
      <Box className="mx-2 rounded-t-md" sx={{ bgcolor: "white" }}>
        <Tabs
          sx={{
            borderBottom: '1px solid #e8e8e8',
            '& .MuiTabs-indicator': {
              backgroundColor: '#084298',
              display: "flex",
              justifyContent: "center"
            },
            '& .MuiTabs-indicatorSpan': {
              maxWidth: 40,
              width: '100%',
              // backgroundColor: '#635ee7',
            },
          }}
          value={value}
          onChange={handleChange}
          variant="scrollable"
          scrollButtons={false}
          aria-label="scrollable prevent tabs example"
        >
          <Tab disableRipple label="Machine View" />
          <Tab disableRipple label="Analyst View" />
          <Tab disableRipple label="Product View" />
          <Tab disableRipple label="SLA Matrix" />
        </Tabs>
        {
          (updating_chart_data && !initial_load) ?
            <Box className={`flex justify-center items-center text-lg`} sx={{ height: calculateRemainingHeight(300) }}>
              <p>Updating Chart Data... Please Wait...</p>
            </Box>
            : <Box>
              <OptimusTabPanel value={value} index={0} >
                <Box className="flex gap-2">
                  <Box className="flex-1">
                    <FileUploaderRipik
                      key={`Machine_View-file_uploader`}
                      file={experiment_files.machine_view.file}
                      name={'machine_view_experiment'}
                      file_update_function={(file) => {
                        handle_experiment_file_upload('machine_view', file, true);
                      }}
                    />
                  </Box>
                  {
                    experiment_files.machine_view.file && <Box>
                      <FileViewer
                        columns={experiment_files.machine_view.columns}
                        table_data={experiment_files.machine_view.table_data}
                        name={`Machine_View_File`}
                      />
                    </Box>
                  }
                  {
                    experiment_files.machine_view.file && <Box>
                      <LoadingButton
                        loading={sharing_loading}
                        onClick={() => {
                          handle_upload_and_share(experiment_files.machine_view, selected_secondary_names_machine_view, "machine_view");
                        }} >Share View</LoadingButton>
                    </Box>
                  }
                </Box>
                {
                  loading_states.machine_view &&
                  <Box my="2">
                    <LinearProgress />
                  </Box>
                }
                {
                  experiment_files.machine_view.paginated_data &&
                  <Box>
                    <FullyIntegratedTimelineChart
                      color_by_options={color_by_options}
                      selected_color={color_by}
                      update_color_by={update_color_by}
                      paginated_x_view_data={experiment_files.machine_view.paginated_data}
                      data_copy_for_filtering={experiment_files.machine_view.paginated_data_copy}
                      update_data_copy_for_filtering={(data) => {
                        set_experiment_files((prev) => {
                          const temp = { ...prev };
                          temp.machine_view.paginated_data_copy = data;
                          return temp;
                        });
                      }}
                      x_key_name={"machine_id"}
                      formatted_json_data={experiment_files.machine_view.formatted_json_data}
                      columns={experiment_files.machine_view.columns}
                      table_data={experiment_files.machine_view.table_data}
                      selected_primary_names={selected_primary_names_machine_view}
                      set_selected_primary_names={set_selected_primary_names_machine_view}
                      primary_filter_name={"product_code"}
                      secondary_filter_name={"test_id"}
                      selected_secondary_filters={selected_secondary_names_machine_view}
                      set_selected_secondary_filters={set_selected_secondary_names_machine_view}
                      set_filtered_in={set_filtered_in}
                      view_name={"machine_view"}
                    // default_page='5'
                    />
                  </Box>
                }
              </OptimusTabPanel>
              <OptimusTabPanel value={value} index={1} >
                <Box className="flex gap-2">
                  <Box className="flex-1">
                    <FileUploaderRipik
                      key={`Analyst_View-file_uploader`}
                      file={experiment_files.analyst_view.file}
                      name={'analyst_view_experiment'}
                      file_update_function={(file) => {
                        handle_experiment_file_upload('analyst_view', file, true);
                      }}
                    />
                  </Box>
                  {
                    experiment_files.analyst_view.file && <Box>
                      <FileViewer
                        columns={experiment_files.analyst_view.columns}
                        table_data={experiment_files.analyst_view.table_data}
                        name={`Analyst_View_File`}
                      />
                    </Box>
                  }
                  {
                    experiment_files.analyst_view.file && <Box>
                      <LoadingButton
                        loading={sharing_loading}
                        onClick={() => {
                          handle_upload_and_share(experiment_files.analyst_view, selected_secondary_names_analyst_view, "analyst_view");
                        }} >Share View</LoadingButton>
                    </Box>
                  }
                </Box>
                {
                  loading_states.analyst_view &&
                  <Box my="2">
                    <LinearProgress />
                  </Box>
                }
                {
                  experiment_files.analyst_view.paginated_data &&
                  <Box>
                    <FullyIntegratedTimelineChart
                      color_by_options={color_by_options}
                      selected_color={color_by}
                      update_color_by={update_color_by}
                      paginated_x_view_data={experiment_files.analyst_view.paginated_data}
                      x_key_name={"analyst_id"}
                      formatted_json_data={experiment_files.analyst_view.formatted_json_data}
                      columns={experiment_files.analyst_view.columns}
                      table_data={experiment_files.analyst_view.table_data}
                      data_copy_for_filtering={experiment_files.analyst_view.paginated_data_copy}
                      update_data_copy_for_filtering={(data) => {
                        set_experiment_files((prev) => {
                          const temp = { ...prev };
                          temp.analyst_view.paginated_data_copy = data;
                          return temp;
                        });
                      }}
                      selected_primary_names={selected_primary_names_analyst_view}
                      set_selected_primary_names={set_selected_primary_names_analyst_view}
                      primary_filter_name={"spec_id"}
                      secondary_filter_name={"test_id"}
                      selected_secondary_filters={selected_secondary_names_analyst_view}
                      set_selected_secondary_filters={set_selected_secondary_names_analyst_view}
                      set_filtered_in={set_filtered_in}
                      view_name={"analyst_view"}
                    // default_page='3'
                    />
                  </Box>
                }
              </OptimusTabPanel>
              <OptimusTabPanel value={value} index={2} >
                <Box className="flex gap-2">
                  <Box className="flex-1">
                    <FileUploaderRipik
                      key={`Product_View-file_uploader`}
                      file={experiment_files.product_view.file}
                      name={'product_view_experiment'}
                      file_update_function={(file) => {
                        handle_experiment_file_upload('product_view', file, true);
                      }}
                    />
                  </Box>
                  {
                    experiment_files.product_view.file && <Box>
                      <FileViewer
                        columns={experiment_files.product_view.columns}
                        table_data={experiment_files.product_view.table_data}
                        name={`Product_View_File`}
                      />
                    </Box>
                  }
                  {
                    experiment_files.product_view.file && <Box>
                      <LoadingButton
                        loading={sharing_loading}
                        onClick={() => {
                          handle_upload_and_share(experiment_files.product_view, selected_secondary_names_product_view, "product_view");
                        }} >Share View</LoadingButton>
                    </Box>
                  }
                </Box>
                {
                  loading_states.product_view &&
                  <Box my="2">
                    <LinearProgress />
                  </Box>
                }
                {
                  experiment_files.product_view.paginated_data &&
                  <Box>
                    <FullyIntegratedTimelineChart
                      color_by_options={color_by_options}
                      selected_color={color_by}
                      update_color_by={update_color_by}
                      paginated_x_view_data={experiment_files.product_view.paginated_data}
                      x_key_name={"product_code"}
                      formatted_json_data={experiment_files.product_view.formatted_json_data}
                      columns={experiment_files.product_view.columns}
                      table_data={experiment_files.product_view.table_data}
                      data_copy_for_filtering={experiment_files.product_view.paginated_data_copy}
                      update_data_copy_for_filtering={(data) => {
                        set_experiment_files((prev) => {
                          const temp = { ...prev };
                          temp.product_view.paginated_data_copy = data;
                          return temp;
                        });
                      }}
                      selected_primary_names={selected_primary_names_product_view}
                      set_selected_primary_names={set_selected_primary_names_product_view}
                      primary_filter_name={"analyst_id"}
                      secondary_filter_name={"test_id"}
                      selected_secondary_filters={selected_secondary_names_product_view}
                      set_selected_secondary_filters={set_selected_secondary_names_product_view}
                      set_filtered_in={set_filtered_in}
                      view_name={"product_view"}
                    // default_page='17'
                    />
                  </Box>
                }
              </OptimusTabPanel>
              <OptimusTabPanel value={value} index={3} >
                <Box className="flex gap-2">
                  <Box className="flex-1">
                    <FileUploaderRipik
                      key={`SLA_MATRIX-file_uploader`}
                      file={experiment_files.sla_matrix.file}
                      name={'sla_matrix_experiment'}
                      file_update_function={(file) => {
                        handle_experiment_file_upload('sla_matrix', file);
                      }}
                    />
                  </Box>
                  {/* {
            experiment_files.sla_matrix.file && <Box>
              <FileViewer
                columns={experiment_files.sla_matrix.columns}
                table_data={experiment_files.sla_matrix.table_data}
                name={`sla_matrix_File`}
              />
            </Box>
          } */}
                </Box>
                {
                  loading_states.sla_matrix &&
                  <Box my="2">
                    <LinearProgress />
                  </Box>
                }
                {
                  experiment_files.sla_matrix.file && experiment_files.sla_matrix.columns && experiment_files.sla_matrix.table_data &&
                  <Box className="w-100">
                    <DataGrid slots={{
                      toolbar: GridToolbar
                    }} rows={experiment_files.sla_matrix.table_data} columns={experiment_files.sla_matrix.columns} />
                  </Box>
                }
              </OptimusTabPanel>
            </Box>
        }
      </Box>
    </Box>
  );
};

export default Experiment;